import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Paper,
  Rating,
  TextField,
  Typography,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import axios from 'axios';
import { format } from 'date-fns';
import { fi } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

import { PublicHeader, PublicFooter } from '../../maintenances/public';
import { theme } from '../../maintenances/public/theme';

const publicAxios = axios.create({
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * The customer's feedback page. Normally mounted at /palaute/:token and fed by the public API.
 *
 * Preview mode (`previewData` given): rendered inside the settings preview dialog with the page
 * payload the backend built for sample data. Nothing is fetched or posted; submitting walks the
 * same branches the backend would pick (Google CTA, low-rating followup, thanks) from the
 * payload so the workshop sees every screen its customers can reach.
 */
function PublicFeedbackView({ previewData = null, previewLanguage = null }) {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const isPreview = previewData !== null;
  const { t } = useTranslation('translation', isPreview && previewLanguage ? { lng: previewLanguage } : undefined);

  const [pageData, setPageData] = useState(previewData);
  const [loading, setLoading] = useState(!isPreview);
  const [error, setError] = useState(null); // 'expired' | 'already_responded' | 'not_found' | 'error'

  // Rating form state
  const [rating, setRating] = useState(null);
  const [hoverRating, setHoverRating] = useState(-1);
  const [comment, setComment] = useState('');
  const [publishConsent, setPublishConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Post-submit state
  const [submitResult, setSubmitResult] = useState(null); // { next_action, google_review_url }
  const [followupRequested, setFollowupRequested] = useState(false);
  const [followupSubmitting, setFollowupSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  // Pre-fill rating from ?rating=N query param
  useEffect(() => {
    if (isPreview) return;
    const preRating = parseInt(searchParams.get('rating'), 10);
    if (preRating >= 1 && preRating <= 5) {
      setRating(preRating);
    }
  }, [searchParams, isPreview]);

  // Preview: new sample data (another language, edited settings) restarts from the rating screen.
  useEffect(() => {
    if (!isPreview) return;
    setPageData(previewData);
    setRating(null);
    setComment('');
    setSubmitResult(null);
    setFollowupRequested(false);
    setShowThankYou(false);
  }, [isPreview, previewData]);

  // Fetch feedback page data
  useEffect(() => {
    if (isPreview) return;
    const fetchData = async () => {
      try {
        const response = await publicAxios.get(`/api/public/feedback/${token}/`);
        setPageData(response.data);
      } catch (err) {
        const status = err.response?.status;
        const errorCode = err.response?.data?.error;
        if (status === 410 && errorCode === 'already_responded') {
          setError('already_responded');
        } else if (status === 410) {
          setError('expired');
        } else if (status === 404) {
          setError('not_found');
        } else {
          setError('error');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, isPreview]);

  const handleSubmit = useCallback(async () => {
    if (!rating || submitting) return;
    if (isPreview) {
      // Same decision the backend makes in public_feedback_respond.
      let nextAction = 'thanks';
      if (pageData.has_google_review_url && rating >= (pageData.google_min_rating ?? 4)) {
        nextAction = 'google_cta';
      } else if (pageData.low_rating_followup_enabled && rating <= 2) {
        nextAction = 'low_rating_followup';
      }
      setSubmitResult({ next_action: nextAction, google_review_url: pageData.google_review_url || '' });
      return;
    }
    setSubmitting(true);
    try {
      const response = await publicAxios.post(`/api/public/feedback/${token}/respond/`, {
        rating,
        comment,
        publish_consent: publishConsent,
      });
      setSubmitResult(response.data);
    } catch (err) {
      const errorCode = err.response?.data?.error;
      if (errorCode === 'already_responded') {
        setError('already_responded');
      } else if (errorCode === 'expired') {
        setError('expired');
      } else {
        setError('error');
      }
    } finally {
      setSubmitting(false);
    }
  }, [token, rating, comment, publishConsent, submitting, isPreview, pageData]);

  const handleGoogleCtaClick = useCallback(() => {
    // Fire-and-forget beacon (not in preview: there is no request to record it on)
    if (!isPreview) {
      publicAxios.post(`/api/public/feedback/${token}/google-cta-clicked/`).catch(() => {});
    }
    // Open Google review in new tab. Also in preview, so the workshop can check its link.
    if (submitResult?.google_review_url) {
      window.open(submitResult.google_review_url, '_blank', 'noopener');
    }
    setShowThankYou(true);
  }, [token, submitResult, isPreview]);

  const handleFollowupRequest = useCallback(async () => {
    if (followupSubmitting) return;
    if (isPreview) {
      setFollowupRequested(true);
      return;
    }
    setFollowupSubmitting(true);
    try {
      await publicAxios.post(`/api/public/feedback/${token}/low-rating-followup/`);
      setFollowupRequested(true);
    } catch {
      // Best effort — still show confirmation
      setFollowupRequested(true);
    } finally {
      setFollowupSubmitting(false);
    }
  }, [token, followupSubmitting, isPreview]);

  // Inside the preview dialog the page fills its frame, not the viewport.
  const pageSx = { minHeight: isPreview ? '100%' : '100vh', bgcolor: theme.colors.background, display: 'flex', flexDirection: 'column' };

  // ---- Loading ----
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: theme.colors.background }}>
        <CircularProgress />
      </Box>
    );
  }

  // ---- Error states ----
  if (error) {
    return (
      <Box data-testid="public-feedback-page" sx={pageSx}>
        <Box component="header" sx={{ backgroundColor: theme.colors.primary, padding: '16px 20px', color: 'white' }}>
          <Box sx={{ maxWidth: '600px', margin: '0 auto' }}>
            <Typography sx={{ fontSize: '20px', fontWeight: '700' }}>
              {t('public_feedback.feedback')}
            </Typography>
          </Box>
        </Box>
        <Container maxWidth="sm" sx={{ py: 6, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Paper
            elevation={0}
            data-testid="feedback-error"
            sx={{
              p: 4,
              borderRadius: theme.radius.xl,
              boxShadow: theme.shadows.md,
              textAlign: 'center',
              width: '100%',
            }}
          >
            {error === 'already_responded' ? (
              <>
                <Typography sx={{ fontSize: '48px', mb: 2 }}>&#10003;</Typography>
                <Typography sx={{ fontSize: '18px', fontWeight: 600, color: theme.colors.text, mb: 1 }}>
                  {t('public_feedback.already_responded_title')}
                </Typography>
                <Typography sx={{ fontSize: '14px', color: theme.colors.textMuted }}>
                  {t('public_feedback.already_responded_message')}
                </Typography>
              </>
            ) : error === 'expired' || error === 'not_found' ? (
              <>
                <Typography sx={{ fontSize: '48px', mb: 2 }}>&#128279;</Typography>
                <Typography sx={{ fontSize: '18px', fontWeight: 600, color: theme.colors.text, mb: 1 }}>
                  {t('public_feedback.link_expired_title')}
                </Typography>
                <Typography sx={{ fontSize: '14px', color: theme.colors.textMuted }}>
                  {t('public_feedback.link_expired_message')}
                </Typography>
              </>
            ) : (
              <>
                <Typography sx={{ fontSize: '18px', fontWeight: 600, color: theme.colors.text, mb: 1 }}>
                  {t('public_feedback.error_loading')}
                </Typography>
              </>
            )}
          </Paper>
        </Container>
        <PublicFooter />
      </Box>
    );
  }

  // ---- Post-submit: Thank you (final state after google CTA dismiss or followup) ----
  if (showThankYou || (submitResult && submitResult.next_action === 'thanks')) {
    return (
      <Box data-testid="public-feedback-page" sx={pageSx}>
        <PublicHeader workshopName={pageData?.workshop_name} logoUrl={pageData?.workshop_logo_url} />
        <Container maxWidth="sm" sx={{ py: 6, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Paper
            elevation={0}
            data-testid="feedback-thank-you"
            sx={{ p: 4, borderRadius: theme.radius.xl, boxShadow: theme.shadows.md, textAlign: 'center', width: '100%' }}
          >
            <Typography sx={{ fontSize: '48px', mb: 2 }}>&#127775;</Typography>
            <Typography sx={{ fontSize: '22px', fontWeight: 700, color: theme.colors.text, mb: 1 }}>
              {t('public_feedback.thank_you_title')}
            </Typography>
            <Typography sx={{ fontSize: '15px', color: theme.colors.textMuted }}>
              {t('public_feedback.thank_you_message')}
            </Typography>
          </Paper>
        </Container>
        <PublicFooter />
      </Box>
    );
  }

  // ---- Post-submit: Google CTA ----
  if (submitResult && submitResult.next_action === 'google_cta') {
    return (
      <Box data-testid="public-feedback-page" sx={pageSx}>
        <PublicHeader workshopName={pageData?.workshop_name} logoUrl={pageData?.workshop_logo_url} />
        <Container maxWidth="sm" sx={{ py: 4, flex: 1 }}>
          <Paper
            elevation={0}
            data-testid="feedback-google-cta"
            sx={{ p: 4, borderRadius: theme.radius.xl, boxShadow: theme.shadows.md, textAlign: 'center' }}
          >
            <Typography sx={{ fontSize: '48px', mb: 2 }}>&#11088;</Typography>
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: theme.colors.text, mb: 2 }}>
              {t('public_feedback.google_cta_title', { rating, workshop: pageData?.workshop_name })}
            </Typography>
            <Typography sx={{ fontSize: '15px', color: theme.colors.textMuted, mb: 3 }}>
              {t('public_feedback.google_cta_message', { workshop: pageData?.workshop_name })}
            </Typography>
            <Button
              variant="contained"
              size="large"
              data-testid="google-review-button"
              onClick={handleGoogleCtaClick}
              sx={{
                backgroundColor: theme.colors.accent,
                color: 'white',
                fontWeight: 600,
                fontSize: '16px',
                borderRadius: theme.radius.lg,
                py: 1.5,
                px: 4,
                width: '100%',
                maxWidth: '360px',
                textTransform: 'none',
                mb: 2,
                '&:hover': { backgroundColor: theme.colors.accentLight },
              }}
            >
              {t('public_feedback.google_cta_button')}
            </Button>
            <Box>
              <Button
                variant="text"
                onClick={() => setShowThankYou(true)}
                sx={{ color: theme.colors.textMuted, textTransform: 'none', fontSize: '14px' }}
              >
                {t('public_feedback.no_thanks')}
              </Button>
            </Box>
          </Paper>
        </Container>
        <PublicFooter />
      </Box>
    );
  }

  // ---- Post-submit: Low rating followup ----
  if (submitResult && submitResult.next_action === 'low_rating_followup') {
    if (followupRequested) {
      return (
        <Box data-testid="public-feedback-page" sx={pageSx}>
          <PublicHeader workshopName={pageData?.workshop_name} logoUrl={pageData?.workshop_logo_url} />
          <Container maxWidth="sm" sx={{ py: 6, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Paper
              elevation={0}
              data-testid="feedback-followup-confirmed"
              sx={{ p: 4, borderRadius: theme.radius.xl, boxShadow: theme.shadows.md, textAlign: 'center', width: '100%' }}
            >
              <Typography sx={{ fontSize: '48px', mb: 2 }}>&#128222;</Typography>
              <Typography sx={{ fontSize: '20px', fontWeight: 700, color: theme.colors.text, mb: 1 }}>
                {t('public_feedback.followup_confirmed_title')}
              </Typography>
              <Typography sx={{ fontSize: '15px', color: theme.colors.textMuted }}>
                {t('public_feedback.followup_confirmed_message')}
              </Typography>
            </Paper>
          </Container>
          <PublicFooter />
        </Box>
      );
    }

    return (
      <Box data-testid="public-feedback-page" sx={pageSx}>
        <PublicHeader workshopName={pageData?.workshop_name} logoUrl={pageData?.workshop_logo_url} />
        <Container maxWidth="sm" sx={{ py: 4, flex: 1 }}>
          <Paper
            elevation={0}
            data-testid="feedback-low-rating-followup"
            sx={{ p: 4, borderRadius: theme.radius.xl, boxShadow: theme.shadows.md, textAlign: 'center' }}
          >
            <Typography sx={{ fontSize: '48px', mb: 2 }}>&#128172;</Typography>
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: theme.colors.text, mb: 2 }}>
              {t('public_feedback.followup_title')}
            </Typography>
            <Typography sx={{ fontSize: '15px', color: theme.colors.textMuted, mb: 3 }}>
              {t('public_feedback.followup_message')}
            </Typography>
            <Button
              variant="contained"
              size="large"
              data-testid="followup-yes-button"
              onClick={handleFollowupRequest}
              disabled={followupSubmitting}
              sx={{
                backgroundColor: theme.colors.accent,
                color: 'white',
                fontWeight: 600,
                fontSize: '16px',
                borderRadius: theme.radius.lg,
                py: 1.5,
                px: 4,
                width: '100%',
                maxWidth: '360px',
                textTransform: 'none',
                mb: 2,
                '&:hover': { backgroundColor: theme.colors.accentLight },
              }}
            >
              {followupSubmitting ? <CircularProgress size={24} color="inherit" /> : t('public_feedback.followup_yes')}
            </Button>
            <Box>
              <Button
                variant="text"
                onClick={() => setShowThankYou(true)}
                sx={{ color: theme.colors.textMuted, textTransform: 'none', fontSize: '14px' }}
              >
                {t('public_feedback.no_thanks')}
              </Button>
            </Box>
          </Paper>
        </Container>
        <PublicFooter />
      </Box>
    );
  }

  // ---- Rating screen (initial state) ----
  return (
    <Box data-testid="public-feedback-page" sx={pageSx}>
      <PublicHeader workshopName={pageData?.workshop_name} logoUrl={pageData?.workshop_logo_url} />

      <Container maxWidth="sm" sx={{ py: 3, flex: 1 }}>
        {/* Visit context */}
        {(pageData?.vehicle_registration || pageData?.vehicle_make_model || pageData?.completion_date) && (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 2.5,
              borderRadius: theme.radius.xl,
              boxShadow: theme.shadows.sm,
            }}
          >
            {pageData.vehicle_registration && (
              <Typography data-testid="feedback-vehicle-reg" sx={{ fontSize: '16px', fontWeight: 700, color: theme.colors.text, letterSpacing: '0.5px', mb: 0.75 }}>
                {pageData.vehicle_registration}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {pageData.vehicle_make_model && (
                <Typography sx={{ fontSize: '13px', color: theme.colors.textMuted }}>
                  {pageData.vehicle_make_model}
                </Typography>
              )}
              {pageData.vehicle_make_model && pageData.completion_date && (
                <Typography sx={{ fontSize: '13px', color: theme.colors.textMuted }}>·</Typography>
              )}
            {pageData.completion_date && (
              <Typography sx={{ fontSize: '13px', color: theme.colors.textMuted }}>
                {t('public_feedback.visit_date')}: {(() => {
                  try { return format(new Date(pageData.completion_date + 'T00:00:00'), 'd.M.yyyy', { locale: fi }); }
                  catch { return pageData.completion_date; }
                })()}
              </Typography>
            )}
            </Box>
          </Paper>
        )}

        {/* Rating card */}
        <Paper
          elevation={0}
          data-testid="feedback-rating-card"
          sx={{
            p: 3,
            borderRadius: theme.radius.xl,
            boxShadow: theme.shadows.md,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: theme.colors.text, mb: 1 }}>
            {t('public_feedback.rating_title')}
          </Typography>

          {pageData?.custom_intro_text && (
            <Typography sx={{ fontSize: '14px', color: theme.colors.textMuted, mb: 2 }}>
              {pageData.custom_intro_text}
            </Typography>
          )}

          {/* Star rating */}
          <Box sx={{ mb: 3, mt: 2 }}>
            <Rating
              name="feedback-rating"
              data-testid="feedback-rating"
              value={rating}
              onChange={(_, newValue) => { if (newValue !== null) setRating(newValue); }}
              onChangeActive={(_, newHover) => setHoverRating(newHover)}
              size="large"
              icon={<StarIcon sx={{ fontSize: '52px' }} />}
              emptyIcon={<StarIcon sx={{ fontSize: '52px', opacity: 0.3 }} />}
              sx={{
                '& .MuiRating-icon': {
                  padding: '4px',
                },
                '& .MuiRating-iconFilled': {
                  color: '#facc15',
                },
                '& .MuiRating-iconHover': {
                  color: '#fbbf24',
                },
              }}
            />
            {(rating || hoverRating > 0) && (
              <Typography sx={{ fontSize: '14px', color: theme.colors.textMuted, mt: 1 }}>
                {hoverRating > 0 ? hoverRating : rating} / 5
              </Typography>
            )}
          </Box>

          {/* Comment field */}
          <TextField
            data-testid="feedback-comment"
            multiline
            rows={3}
            fullWidth
            placeholder={t('public_feedback.comment_placeholder')}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: theme.radius.lg,
                fontSize: '15px',
              },
            }}
          />

          {/* Consent to publish on the workshop's website (Nettisivut), first name only.
              Shown only when the workshop is in the Nettisivut pilot. */}
          {pageData?.website_publish_consent && (
            <FormControlLabel
              control={(
                <Checkbox
                  checked={publishConsent}
                  onChange={(e) => setPublishConsent(e.target.checked)}
                  data-testid="feedback-publish-consent"
                />
              )}
              label={t('public_feedback.publish_consent_label')}
              sx={{ mb: 2, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { fontSize: '14px', color: theme.colors.textMuted, pt: '9px' } }}
            />
          )}

          {/* Submit button */}
          <Button
            variant="contained"
            size="large"
            data-testid="feedback-submit"
            onClick={handleSubmit}
            disabled={!rating || submitting}
            sx={{
              backgroundColor: theme.colors.accent,
              color: 'white',
              fontWeight: 600,
              fontSize: '16px',
              borderRadius: theme.radius.lg,
              py: 1.5,
              width: '100%',
              textTransform: 'none',
              '&:hover': { backgroundColor: theme.colors.accentLight },
              '&.Mui-disabled': { backgroundColor: '#cbd5e1', color: '#94a3b8' },
            }}
          >
            {submitting ? <CircularProgress size={24} color="inherit" /> : t('public_feedback.submit_button')}
          </Button>
        </Paper>
      </Container>

      <PublicFooter />
    </Box>
  );
}

export default PublicFeedbackView;
